import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  Flag,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiFetch, getToken } from "@/api";
import { getChapters, getSubjects } from "@/data/curriculum";
import { KIND_META, taskAction } from "@/lib/plannerActions";
import AcademicContextBar from "@/components/AcademicContextBar";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import Paywall from "@/components/Paywall";
import { inputClass } from "@/components/AuthLayout";

/* Study Planner — exam-date-driven. The student's saved Board/Class, selected
   subjects, exam date and study time are the ONLY inputs; the backend engine
   validates every task against the real curriculum. */

const MINUTE_OPTIONS = [30, 60, 90, 120];

const pill = (active) =>
  `rounded-full border-2 px-4 py-2 text-xs font-bold transition-[border-color,background-color] duration-200 ${
    active
      ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
      : "border-[#17201A]/10 bg-[#F8FAF7] text-[#17201A]/60 hover:border-[#16A34A]/40"
  }`;

const fmtDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
const fmtWeekday = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" });
const fmtExam = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const TaskRow = ({ task, me, onToggle }) => {
  const navigate = useNavigate();
  const meta = KIND_META[task.kind] || KIND_META.revise;
  const Icon = meta.icon;
  const action = taskAction(task, me);
  return (
    <div data-testid={`plan-task-${task.id}`} className="flex items-center gap-3 py-3.5">
      <button
        data-testid={`task-toggle-${task.id}`}
        aria-label={task.done ? "Mark task not done" : "Mark task done"}
        onClick={() => onToggle(task)}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
          task.done ? "border-[#16A34A] bg-[#16A34A]" : "border-[#E2E8F0] bg-white hover:border-[#16A34A]"
        }`}
      >
        {task.done && <CheckCircle2 className="h-4 w-4 text-white" />}
      </button>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#16A34A]/15 bg-[#16A34A]/8 text-[#16A34A]">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-bold ${task.done ? "text-[#94A39B] line-through" : "text-[#17201A]"}`}>
          {task.title}
        </p>
        <p className="text-xs font-semibold text-[#647067]">
          {task.subject}
          {task.chapter ? ` · ${task.chapter}` : ""} · {task.minutes} min · {meta.label}
        </p>
      </div>
      {action && !task.done && (
        <button
          data-testid={`task-action-${task.id}`}
          onClick={() => navigate(action.to, action.state ? { state: action.state } : undefined)}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#16A34A]/30 px-3 py-1.5 text-[11px] font-bold text-[#16A34A] transition-colors duration-200 hover:bg-[#16A34A] hover:text-white"
        >
          Start
          <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const StudyPlanner = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plan, setPlan] = useState(null);
  const [legacy, setLegacy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeDay, setActiveDay] = useState(null);

  // form state
  const [examTarget, setExamTarget] = useState("");
  const [examDate, setExamDate] = useState("");
  const [dateOpen, setDateOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [dailyMinutes, setDailyMinutes] = useState(60);
  const [priorities, setPriorities] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/login", { replace: true });
      return;
    }
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        // preselect profile subjects that are valid for the saved Board/Class
        const valid = u.board && u.klass ? getSubjects(u.board === "CBSE" ? "CBSE" : "CISCE", String(u.klass)) : [];
        setSubjects((u.subjects || []).filter((s) => valid.includes(s)));
      })
      .catch(() => {});
    apiFetch("/study-planner")
      .then((d) => {
        setPlan(d.plan);
        setLegacy(Boolean(d.legacy));
        if (d.plan) setActiveDay(d.plan.examToday ? d.plan.examDay?.date : d.plan.today);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileReady = Boolean(me?.board && me?.klass);
  const boardKey = me?.board === "CBSE" ? "CBSE" : "CISCE";
  // ALL subjects valid for the saved Board/Class — never profile-restricted
  const subjectChoices = profileReady ? getSubjects(boardKey, String(me.klass)) : [];

  // "Change Data" — update context, keep only still-valid selected subjects
  const handleContextChange = (newBoard, newKlass) => {
    const bk = newBoard === "CBSE" ? "CBSE" : "CISCE";
    setMe((prev) => (prev ? { ...prev, board: newBoard, klass: String(newKlass) } : prev));
    const valid = getSubjects(bk, String(newKlass));
    setSubjects((prev) => prev.filter((s) => valid.includes(s)));
  };

  const toggleSubject = (s) =>
    setSubjects((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const generate = async () => {
    if (!subjects.length) {
      toast.error("Pick at least one subject");
      return;
    }
    if (!examDate) {
      toast.error("Pick your exam date — the plan is built around it");
      return;
    }
    setGenerating(true);
    try {
      const chapters = {};
      subjects.forEach((s) => {
        chapters[s] = getChapters(boardKey, String(me.klass), s);
      });
      const d = await apiFetch("/study-planner/generate", {
        method: "POST",
        body: {
          examTarget: examTarget || "Upcoming exam",
          examDate,
          subjects,
          dailyMinutes,
          priorities,
          chapters,
        },
      });
      setPlan(d.plan);
      setLegacy(false);
      setActiveDay(d.plan.examToday ? d.plan.examDay?.date : d.plan.today);
      setShowForm(false);
      toast.success(
        d.plan.generationMethod === "fallback"
          ? "Plan ready (built from your syllabus — AI was busy)"
          : "Your study plan is ready"
      );
    } catch (err) {
      if (err.message === "FREE_LIMIT_REACHED") setPaywallOpen(true);
      else if (err.message === "COMPLETE_PROFILE")
        toast.error("Complete your academic profile (board & class) first");
      else toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleTask = async (task) => {
    const done = !task.done;
    setPlan((p) => ({
      ...p,
      days: p.days.map((d) => ({
        ...d,
        tasks: d.tasks.map((t) => (t.id === task.id ? { ...t, done } : t)),
      })),
    }));
    try {
      await apiFetch("/study-planner/task-toggle", {
        method: "POST",
        body: { taskId: task.id, done },
      });
    } catch {
      toast.error("Couldn't save — try again");
    }
  };

  const examDayActive = Boolean(plan?.examDay && activeDay === plan.examDay.date);
  const viewDay = plan?.days?.find((d) => d.date === activeDay) ||
    plan?.days?.find((d) => d.date === plan.today) || plan?.days?.[0] || null;

  const form = (
    <div data-testid="planner-form" className="mt-8 rounded-[2rem] border border-[#E3ECE6] bg-white p-7 shadow-sm sm:p-9">
      {/* Saved academic context — source of truth, changeable via Change Data */}
      {profileReady && (
        <div className="mb-5">
          <AcademicContextBar
            board={me.board}
            klass={me.klass}
            onChanged={handleContextChange}
            testId="planner-context"
          />
        </div>
      )}
      <label className="mb-1.5 block text-sm font-bold">What are you preparing for?</label>
      <input
        data-testid="planner-exam-target"
        value={examTarget}
        onChange={(e) => setExamTarget(e.target.value)}
        placeholder="e.g. Class 10 Board Exams"
        className={`${inputClass} mb-5`}
      />
      <label className="mb-1.5 block text-sm font-bold">
        Exam date <span className="text-[#DC2626]">*</span>
      </label>
      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="planner-exam-date"
            className={`${inputClass} mb-5 flex items-center justify-between text-left ${
              examDate ? "" : "text-[#94A39B]"
            }`}
          >
            {examDate ? format(new Date(`${examDate}T00:00:00`), "d MMM yyyy") : "Pick your exam date"}
            <CalendarIcon className="h-4 w-4 text-[#647067]" />
          </button>
        </PopoverTrigger>
        <PopoverContent data-testid="planner-date-popover" className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={examDate ? new Date(`${examDate}T00:00:00`) : undefined}
            onSelect={(d) => {
              setExamDate(d ? format(d, "yyyy-MM-dd") : "");
              setDateOpen(false);
            }}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <label className="mb-1.5 block text-sm font-bold">Subjects (only these will appear in your plan)</label>
      <div className="mb-5 flex flex-wrap gap-2">
        {subjectChoices.map((s) => (
          <button
            key={s}
            data-testid={`planner-subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => toggleSubject(s)}
            className={pill(subjects.includes(s))}
          >
            {s}
          </button>
        ))}
      </div>
      <label className="mb-1.5 block text-sm font-bold">Study time per day</label>
      <div data-testid="planner-minutes" className="mb-5 flex flex-wrap gap-2">
        {MINUTE_OPTIONS.map((m) => (
          <button
            key={m}
            data-testid={`planner-minutes-${m}`}
            onClick={() => setDailyMinutes(m)}
            className={pill(dailyMinutes === m)}
          >
            {m} min
          </button>
        ))}
      </div>
      <label className="mb-1.5 block text-sm font-bold">Priorities (optional)</label>
      <textarea
        data-testid="planner-priorities"
        rows={2}
        value={priorities}
        onChange={(e) => setPriorities(e.target.value)}
        placeholder="e.g. Finish Life Processes before the weekend"
        className={`${inputClass} mb-6 resize-none`}
      />
      <button
        data-testid="planner-generate-btn"
        data-magnetic="true"
        onClick={generate}
        disabled={generating || !profileReady}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,opacity] duration-200 hover:bg-[#15803D] disabled:opacity-50"
      >
        {generating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Building your plan...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            Generate My Plan
          </>
        )}
      </button>
      <p className="mt-3 text-center text-xs font-semibold text-[#647067]">
        Uses 1 practice generation from your plan · built from your exam date, subjects and syllabus
      </p>
    </div>
  );

  return (
    <div
      data-testid="study-planner-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
        <button
          data-testid="planner-back-btn"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] transition-colors duration-200 hover:border-[#FACC15]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </button>
        <UserAvatar />
      </header>

      <main className="relative mx-auto max-w-2xl px-6 pb-20 pt-4">
        {loading ? (
          <div data-testid="planner-loading" className="flex justify-center pt-24">
            <Loader2 className="h-7 w-7 animate-spin text-[#16A34A]" />
          </div>
        ) : !plan || showForm ? (
          <>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#16A34A]/15 bg-[#16A34A]/8 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#14532D]">
              <CalendarCheck className="h-3.5 w-3.5" />
              Study Planner
            </span>
            <h1 data-testid="planner-headline" className="mt-4 font-display text-3xl font-extrabold sm:text-4xl">
              A realistic path to <span className="italic text-[#16A34A]">exam day</span>
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-[#647067] sm:text-base">
              Tell Abhyas your exam date and subjects. It sequences coverage,
              practice, review and revision across the days you actually have.
            </p>
            {legacy && (
              <p data-testid="planner-legacy-note" className="mt-4 rounded-xl border border-[#FACC15]/40 bg-[#FEF9C3]/60 px-4 py-3 text-xs font-bold text-[#92400E]">
                Your old plan used the previous format — generate a fresh one for accurate dates.
              </p>
            )}
            {!profileReady && me && (
              <p data-testid="planner-profile-warning" className="mt-4 rounded-xl border border-[#FACC15]/40 bg-[#FEF9C3]/60 px-4 py-3 text-xs font-bold text-[#92400E]">
                Complete your academic profile (board &amp; class) before generating a plan.
              </p>
            )}
            {form}
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#16A34A]/15 bg-[#16A34A]/8 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#14532D]">
                  <CalendarCheck className="h-3.5 w-3.5" />
                  Study Planner
                </span>
                <h1 data-testid="planner-title" className="mt-3 font-display text-3xl font-extrabold">
                  {plan.examTarget}
                </h1>
                <p data-testid="planner-meta" className="mt-1 text-sm font-semibold text-[#647067]">
                  Class {plan.klass} · {plan.subjects.join(", ")}
                  {plan.examDate ? ` · Exam: ${fmtExam(plan.examDate)}` : ""}
                  {" · "}{plan.dailyMinutes} min/day
                </p>
              </div>
              <button
                data-testid="planner-regenerate-btn"
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 rounded-full border border-[#E3ECE6] bg-white px-5 py-2.5 text-xs font-bold text-[#17201A] transition-colors duration-200 hover:border-[#16A34A]/40"
              >
                <RefreshCw className="h-3.5 w-3.5 text-[#16A34A]" />
                New plan
              </button>
            </div>

            {/* Expired plan — exam date has passed */}
            {plan.expired && (
              <div
                data-testid="planner-expired"
                className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#FACC15]/50 bg-[#FEF9C3]/60 px-5 py-4"
              >
                <p className="text-xs font-bold leading-relaxed text-[#92400E]">
                  This plan's exam date has passed. Create a new plan for your
                  next exam.
                </p>
                <button
                  data-testid="planner-expired-new-plan"
                  onClick={() => setShowForm(true)}
                  className="rounded-full bg-[#16A34A] px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
                >
                  New plan
                </button>
              </div>
            )}

            {/* Weekly focus — derived from the SAME active plan */}
            {plan.weeklyFocus?.length > 0 && (
              <div data-testid="planner-weekly-focus" className="mt-8 rounded-[2rem] border border-[#E3ECE6] bg-white p-7">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#B45309]">
                  Focus for this plan
                </p>
                <ul className="mt-3 space-y-2">
                  {plan.weeklyFocus.map((f, i) => (
                    <li key={i} data-testid={`weekly-focus-${i}`} className="flex gap-2.5 text-sm font-semibold text-[#17201A]">
                      <span className="font-display font-bold text-[#16A34A]">{i + 1}.</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Date tabs — the actual preparation window + EXAM DAY marker */}
            <div data-testid="planner-day-tabs" className="mt-8 flex gap-1.5 overflow-x-auto pb-1">
              {plan.days.map((d) => {
                const doneCount = d.tasks.filter((t) => t.done).length;
                const active = viewDay?.date === d.date && !examDayActive;
                return (
                  <button
                    key={d.date}
                    data-testid={`planner-day-${d.date}`}
                    onClick={() => setActiveDay(d.date)}
                    className={`flex shrink-0 flex-col items-center rounded-2xl border-2 px-4 py-2.5 transition-colors duration-200 ${
                      active
                        ? "border-[#16A34A] bg-[#16A34A]/10"
                        : d.status === "past"
                          ? "border-[#E3ECE6] bg-white opacity-55 hover:border-[#16A34A]/30"
                          : "border-[#E3ECE6] bg-white hover:border-[#16A34A]/40"
                    }`}
                  >
                    <span className="text-xs font-extrabold">
                      {fmtDay(d.date)}
                      {d.status === "today" && <span className="ml-1 text-[#16A34A]">•</span>}
                    </span>
                    <span className="mt-0.5 text-[10px] font-bold text-[#647067]">
                      {fmtWeekday(d.date)} · {doneCount}/{d.tasks.length}
                    </span>
                  </button>
                );
              })}
              {plan.examDay && (
                <button
                  data-testid="planner-exam-day-tab"
                  onClick={() => setActiveDay(plan.examDay.date)}
                  className={`flex shrink-0 flex-col items-center rounded-2xl border-2 px-4 py-2.5 transition-colors duration-200 ${
                    examDayActive
                      ? "border-[#14532D] bg-[#14532D] text-white"
                      : "border-[#14532D]/30 bg-[#14532D]/5 text-[#14532D] hover:border-[#14532D]/60"
                  }`}
                >
                  <span className="inline-flex items-center gap-1 text-xs font-extrabold">
                    <Flag className="h-3 w-3" />
                    {fmtDay(plan.examDay.date)}
                  </span>
                  <span className={`mt-0.5 text-[10px] font-bold ${examDayActive ? "text-white/70" : "text-[#647067]"}`}>
                    Exam day
                  </span>
                </button>
              )}
            </div>

            {/* EXAM DAY panel — no tasks, no checkboxes, no Start buttons */}
            {examDayActive ? (
              <div
                data-testid="exam-day-panel"
                className="mt-4 rounded-[2rem] border-2 border-[#14532D]/20 bg-white p-10 text-center"
              >
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#14532D]/10 text-[#14532D]">
                  <Flag className="h-6 w-6" />
                </span>
                <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-wide">
                  Exam Day
                </h2>
                <p className="mx-auto mt-2 max-w-sm text-sm font-semibold leading-relaxed text-[#647067]">
                  Your preparation plan ends here. Focus on your exam today —
                  you're ready.
                </p>
              </div>
            ) : (
            /* Task list */
            <div
              data-testid="planner-task-list"
              className="mt-4 divide-y divide-[#E3ECE6] rounded-[2rem] border border-[#E3ECE6] bg-white px-7 py-3"
            >
              <p className="pt-3 text-xs font-bold uppercase tracking-[0.18em] text-[#647067]">
                {viewDay ? `${fmtWeekday(viewDay.date)}, ${fmtDay(viewDay.date)}` : ""}
                {viewDay?.status === "today" && <span className="ml-2 text-[#16A34A]">Today</span>}
                {viewDay?.status === "past" && <span className="ml-2 text-[#94A39B]">Passed</span>}
              </p>
              {(viewDay?.tasks || []).map((t) => (
                <TaskRow key={t.id} task={t} me={me} onToggle={toggleTask} />
              ))}
              {viewDay && viewDay.tasks.length === 0 && (
                <p className="py-5 text-sm font-semibold text-[#647067]">
                  Rest day — nothing scheduled.
                </p>
              )}
            </div>
            )}
          </>
        )}
      </main>
      <Paywall open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
};

export default StudyPlanner;
